import { GuidelineTemplate } from '@/types/community/guideline';

export const GUIDELINE_TEMPLATES: GuidelineTemplate[] = [
  // General Conduct
  {
    category: 'general_conduct',
    subPolicy: 'noise_levels',
    title: 'Noise Levels',
    description: 'Guidelines for maintaining reasonable noise levels in the community',
    defaultContent: `Residents are requested to maintain reasonable noise levels, especially between 10 PM and 8 AM, to ensure a peaceful environment for everyone. 

Key Guidelines:
• Keep music and TV volumes at reasonable levels
• Avoid loud conversations in common areas during quiet hours
• Use headphones when listening to music in shared spaces
• Be mindful of children playing and their noise levels
• Report excessive noise to the administration

Excessive noise complaints may result in warnings or fines.`
  },
  {
    category: 'general_conduct',
    subPolicy: 'waste_disposal',
    title: 'Waste Disposal',
    description: 'Proper waste management and disposal procedures',
    defaultContent: `All household waste must be securely bagged and disposed of in the designated bins. 

Guidelines:
• Separate recyclables according to the community recycling program
• Use proper waste bins and keep them covered
• Large items or hazardous waste require special arrangements for disposal
• Do not leave waste outside your apartment door
• Report any issues with waste collection to the administration

Recyclables should be cleaned and sorted properly before disposal.`
  },
  {
    category: 'general_conduct',
    subPolicy: 'electricity_usage',
    title: 'Electricity Usage',
    description: 'Guidelines for responsible electricity consumption',
    defaultContent: `Residents are encouraged to use electricity responsibly to maintain reasonable utility costs for all community members.

Guidelines:
• Turn off lights and appliances when not in use
• Use energy-efficient appliances and lighting
• Report electrical issues immediately to maintenance
• Avoid overloading electrical circuits
• Use air conditioning and heating systems efficiently

Excessive electricity usage may affect common area costs shared by all residents.`
  },
  {
    category: 'general_conduct',
    subPolicy: 'water_usage',
    title: 'Water Usage',
    description: 'Guidelines for responsible water consumption',
    defaultContent: `Water conservation is important for the community and environment.

Guidelines:
• Report water leaks immediately to maintenance
• Use water-efficient fixtures and appliances
• Avoid leaving taps running unnecessarily
• Water plants during appropriate hours only
• Report any water quality issues

Conserving water helps maintain reasonable utility costs for all residents.`
  },
  {
    category: 'general_conduct',
    subPolicy: 'pets_maintenance',
    title: 'Pets Maintenance',
    description: 'Guidelines for pet ownership and maintenance',
    defaultContent: `Pet owners are responsible for their pets' behavior and maintenance.

Guidelines:
• Keep pets on leash in common areas
• Clean up after pets immediately
• Ensure pets do not disturb other residents
• Register pets with the administration
• Follow vaccination and health requirements
• Report any pet-related incidents

Pet owners may be held responsible for any damage or disturbances caused by their pets.`
  },

  // Parking & Vehicles
  {
    category: 'parking_vehicles',
    subPolicy: 'resident_parking',
    title: 'Resident Parking',
    description: 'Guidelines for resident parking',
    defaultContent: `Each unit is allocated one parking spot. 

Guidelines:
• Vehicles must be registered with the office
• Display the community parking permit clearly
• Park only in your designated spot
• Keep your parking area clean and free of debris
• Report any parking violations to administration
• Follow parking lot speed limits

Unauthorized parking may result in towing at owner's expense.`
  },
  {
    category: 'parking_vehicles',
    subPolicy: 'visitor_parking',
    title: 'Visitor Parking',
    description: 'Guidelines for visitor parking',
    defaultContent: `Visitor parking is available in designated areas only.

Guidelines:
• Visitor parking is time-limited (check posted signs)
• Visitors must register at the office or with security
• Do not park in resident-designated spots
• Follow all posted parking regulations
• Report any parking issues to administration
• Visitor parking is on a first-come, first-served basis

Unauthorized parking in visitor spots may result in towing.`
  },
  {
    category: 'parking_vehicles',
    subPolicy: 'vehicle_registration',
    title: 'Vehicle Registration',
    description: 'Guidelines for vehicle registration and permits',
    defaultContent: `All vehicles must be properly registered with the community.

Guidelines:
• Register all vehicles with the administration office
• Provide current insurance and registration documents
• Update registration when vehicle information changes
• Display parking permits as required
• Report vehicle sales or transfers immediately
• Follow community vehicle standards

Unregistered vehicles may be subject to towing or fines.`
  },
  {
    category: 'parking_vehicles',
    subPolicy: 'speed_limits',
    title: 'Speed Limits',
    description: 'Speed limits and traffic rules within the community',
    defaultContent: `Speed limits are strictly enforced for community safety.

Guidelines:
• Maximum speed limit is 15 mph in all community areas
• Yield to pedestrians at all times
• Follow all traffic signs and signals
• No reckless driving or racing
• Report traffic violations to administration
• Be extra cautious in parking areas

Speed violations may result in warnings, fines, or loss of parking privileges.`
  },
  {
    category: 'parking_vehicles',
    subPolicy: 'loading_unloading',
    title: 'Loading and Unloading',
    description: 'Guidelines for loading and unloading activities',
    defaultContent: `Loading and unloading areas are designated for temporary use only.

Guidelines:
• Use designated loading zones for deliveries
• Limit loading/unloading time to 30 minutes maximum
• Do not block traffic or other parking spaces
• Clean up any debris or spills immediately
• Report any damage to loading areas
• Follow posted time restrictions

Extended use of loading areas may result in fines or towing.`
  },

  // Safety & Security
  {
    category: 'safety_security',
    subPolicy: 'emergency_procedures',
    title: 'Emergency Procedures',
    description: 'Emergency response and evacuation procedures',
    defaultContent: `Familiarize yourself with emergency procedures for community safety.

Guidelines:
• Know the location of emergency exits and fire extinguishers
• Follow evacuation procedures during emergencies
• Do not use elevators during fire emergencies
• Report emergencies immediately to 911 and administration
• Keep emergency contact information updated
• Participate in emergency drills when scheduled

Emergency procedures are posted in common areas and should be reviewed regularly.`
  },
  {
    category: 'safety_security',
    subPolicy: 'fire_safety',
    title: 'Fire Safety',
    description: 'Fire safety guidelines and prevention measures',
    defaultContent: `Fire safety is everyone's responsibility in the community.

Guidelines:
• Do not tamper with smoke detectors or fire alarms
• Keep fire exits and stairwells clear at all times
• Report any fire hazards immediately
• Do not store flammable materials in common areas
• Follow posted fire safety regulations
• Know the location of fire extinguishers

Fire safety violations may result in fines or legal action.`
  },
  {
    category: 'safety_security',
    subPolicy: 'access_control',
    title: 'Access Control',
    description: 'Guidelines for building access and security',
    defaultContent: `Access control systems are in place for community security.

Guidelines:
• Do not share access cards or codes with unauthorized persons
• Report lost or stolen access cards immediately
• Do not prop open secured doors
• Follow visitor registration procedures
• Report suspicious activity to security or administration
• Keep access points clear and unobstructed

Unauthorized access may result in loss of privileges or legal action.`
  },
  {
    category: 'safety_security',
    subPolicy: 'surveillance',
    title: 'Surveillance and Monitoring',
    description: 'Guidelines for surveillance systems and monitoring',
    defaultContent: `Surveillance systems are in place for community security and safety.

Guidelines:
• Surveillance cameras are active in common areas
• Do not tamper with or obstruct surveillance equipment
• Report any suspicious activity to administration
• Surveillance footage may be used for security purposes
• Respect privacy in private areas
• Follow all security protocols

Surveillance systems help maintain community safety and security.`
  },
  {
    category: 'safety_security',
    subPolicy: 'visitor_management',
    title: 'Visitor Management',
    description: 'Guidelines for visitor registration and management',
    defaultContent: `All visitors must be properly registered and managed.

Guidelines:
• Register all visitors at the office or with security
• Visitors must be accompanied by residents in common areas
• Follow posted visitor hours and restrictions
• Report any visitor-related issues immediately
• Visitors are subject to community rules and regulations
• Limit the number of visitors per unit as posted

Unauthorized visitors may be asked to leave the premises.`
  },

  // Announcements & Notifications
  {
    category: 'announcements_notifications',
    subPolicy: 'communication_channels',
    title: 'Communication Channels',
    description: 'Official communication channels and methods',
    defaultContent: `Official communications are sent through designated channels.

Guidelines:
• Check community bulletin boards regularly
• Read all posted notices and announcements
• Provide current contact information to administration
• Respond to official communications promptly
• Use official channels for community-related inquiries
• Report any communication issues to administration

Important announcements are posted in common areas and sent via official channels.`
  },
  {
    category: 'announcements_notifications',
    subPolicy: 'notice_periods',
    title: 'Notice Periods',
    description: 'Required notice periods for various activities',
    defaultContent: `Proper notice periods are required for various community activities.

Guidelines:
• Maintenance work requires 24-hour notice unless emergency
• Community events require 48-hour notice
• Policy changes require 30-day notice
• Emergency situations may require immediate action
• Check notice boards for current information
• Contact administration for clarification on notice requirements

Proper notice helps ensure smooth community operations.`
  },
  {
    category: 'announcements_notifications',
    subPolicy: 'urgent_notifications',
    title: 'Urgent Notifications',
    description: 'Procedures for urgent and emergency notifications',
    defaultContent: `Urgent notifications are sent for emergency or critical situations.

Guidelines:
• Urgent notifications may be sent via multiple channels
• Respond to urgent notifications immediately
• Follow instructions in urgent notifications
• Report emergencies to 911 and administration
• Keep emergency contact information current
• Share urgent information with household members

Urgent notifications are sent only for critical situations requiring immediate attention.`
  },
  {
    category: 'announcements_notifications',
    subPolicy: 'event_announcements',
    title: 'Event Announcements',
    description: 'Guidelines for community event announcements',
    defaultContent: `Community events are announced through official channels.

Guidelines:
• Check community calendar for upcoming events
• Register for events that require registration
• Follow event-specific rules and guidelines
• Report any event-related issues to administration
• Participate in community events when possible
• Share event information with household members

Community events help build a strong and connected community.`
  },

  // Common Expenses
  {
    category: 'common_expenses',
    subPolicy: 'utility_sharing',
    title: 'Utility Sharing',
    description: 'Guidelines for shared utility costs and billing',
    defaultContent: `Common utilities are shared among all community residents.

Guidelines:
• Common area utilities are included in maintenance fees
• Individual unit utilities are billed separately
• Report utility issues immediately to maintenance
• Conserve energy and water to control costs
• Follow utility usage guidelines
• Pay utility bills on time to avoid late fees

Shared utility costs help maintain reasonable fees for all residents.`
  },
  {
    category: 'common_expenses',
    subPolicy: 'maintenance_fees',
    title: 'Maintenance Fees',
    description: 'Guidelines for maintenance fee collection and usage',
    defaultContent: `Maintenance fees cover common area maintenance and services.

Guidelines:
• Maintenance fees are due on the first of each month
• Late fees apply after the 15th of the month
• Fees cover common area maintenance and services
• Special assessments may be required for major projects
• Contact administration for payment arrangements if needed
• Keep payment information current

Maintenance fees are essential for community maintenance and services.`
  },
  {
    category: 'common_expenses',
    subPolicy: 'insurance_coverage',
    title: 'Insurance Coverage',
    description: 'Guidelines for community insurance coverage',
    defaultContent: `Community insurance covers common areas and building structures.

Guidelines:
• Community insurance covers common areas and building structures
• Individual unit contents are not covered by community insurance
• Report any damage immediately to administration
• Follow insurance claim procedures for covered incidents
• Maintain adequate personal insurance for unit contents
• Contact administration for insurance-related questions

Community insurance helps protect the building and common areas.`
  },
  {
    category: 'common_expenses',
    subPolicy: 'reserve_fund',
    title: 'Reserve Fund',
    description: 'Guidelines for reserve fund management and usage',
    defaultContent: `The reserve fund is maintained for major repairs and improvements.

Guidelines:
• Reserve fund is used for major repairs and capital improvements
• Reserve fund studies are conducted regularly
• Special assessments may be required for major projects
• Reserve fund usage is approved by the board
• Contact administration for reserve fund information
• Reserve fund helps maintain property value

The reserve fund ensures funds are available for major community projects.`
  },

  // Maintenance Requests
  {
    category: 'maintenance_requests',
    subPolicy: 'request_procedures',
    title: 'Request Procedures',
    description: 'Procedures for submitting maintenance requests',
    defaultContent: `Follow proper procedures for submitting maintenance requests.

Guidelines:
• Submit maintenance requests through official channels
• Provide detailed descriptions of issues
• Include photos when possible for better understanding
• Follow up on requests if not addressed promptly
• Report emergency maintenance issues immediately
• Keep records of all maintenance requests

Proper request procedures help ensure timely maintenance service.`
  },
  {
    category: 'maintenance_requests',
    subPolicy: 'priority_levels',
    title: 'Priority Levels',
    description: 'Guideline for maintenance request priority levels',
    defaultContent: `Maintenance requests are prioritized based on urgency and impact.

Guidelines:
• Emergency requests (safety issues) are addressed immediately
• Urgent requests (affecting daily life) within 24 hours
• Routine requests within 3-5 business days
• Preventive maintenance is scheduled regularly
• Contact administration for priority clarification
• Follow up on urgent requests if not addressed

Priority levels help ensure critical issues are addressed first.`
  },
  {
    category: 'maintenance_requests',
    subPolicy: 'response_times',
    title: 'Response Times',
    description: 'Expected response times for different types of requests',
    defaultContent: `Response times vary based on request type and priority.

Guidelines:
• Emergency requests: Immediate response
• Urgent requests: Within 24 hours
• Routine requests: Within 3-5 business days
• Preventive maintenance: Scheduled in advance
• Contact administration for status updates
• Follow up if response times are exceeded

Response times help set expectations for maintenance service.`
  },
  {
    category: 'maintenance_requests',
    subPolicy: 'follow_up',
    title: 'Follow-up Procedures',
    description: 'Procedures for following up on maintenance requests',
    defaultContent: `Follow up on maintenance requests to ensure completion.

Guidelines:
• Follow up on requests that exceed expected response times
• Contact administration for status updates
• Provide feedback on completed maintenance work
• Report any issues with completed work
• Keep records of all maintenance interactions
• Escalate unresolved issues to administration

Follow-up procedures help ensure quality maintenance service.`
  }
];

export const getTemplatesByCategory = (category: string) => {
  return GUIDELINE_TEMPLATES.filter(template => template.category === category);
};

export const getTemplateBySubPolicy = (category: string, subPolicy: string) => {
  return GUIDELINE_TEMPLATES.find(template => 
    template.category === category && template.subPolicy === subPolicy
  );
}; 